import { useAuth0 } from "@auth0/auth0-react";
import { useRoutes } from "react-router-dom";
import { useEffect, useState } from "react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { getRoutes } from "./services/routing/routing.service";
import { getLocalStorageUser } from "./services/session/auth.service";
import { createInstance, MatomoProvider } from "@jonkoops/matomo-tracker-react";
import { MatomoInstance } from "@jonkoops/matomo-tracker-react/lib/types";
import { MatomoService } from "./services/matomo/matomo.service";
import { ThemeService } from "./services/theme/ThemeService.service";

const App = () => {
	const reactQueryDevtoolsEnabled = false;
	const element = useRoutes(getRoutes());
	const auth0 = useAuth0();

	/**
	 * MATOMO: create instance
	 * Create the instance once the user is logged in, and the localStorage has been set
	 */

	const [matomoInstance, setMatomoInstance] = useState<MatomoInstance | undefined>();

	useEffect(() => {
		if (auth0.isAuthenticated && getLocalStorageUser()?.id) {
			/**
			 * Theme:
			 * Now that the user has logged in, update the theme based on his brand identity
			 */
			ThemeService.setTheme();

			// Setup matomo instance
			setMatomoInstance(
				createInstance({
					urlBase: process.env.REACT_APP_MATOMO_URL_BASE!,
					siteId: Number(process.env.REACT_APP_MATOMO_SITE_ID!),
					userId: getLocalStorageUser().id!.toString(),
					disabled: false, // optional, false by default. Makes all tracking calls no-ops if set to true.
					heartBeat: {
						active: true,
						seconds: 10, // Requires a page to be actively viewed for 10 seconds for any heart beat request to be sent.
					},
					linkTracking: false,
				})
			);
		}
	}, [auth0.isAuthenticated, getLocalStorageUser()?.id]);

	/**
	 * MATOMO: login event
	 * Send the login event, once the user is authenticated, and the matomo instance has been created.
	 * The event is captured on the Main page, since at this point (inside App) we are not inside the Matomo provider tree.
	 */

	useEffect(() => {
		if (matomoInstance) {
			MatomoService.setLoginEvent();
		}
	}, [matomoInstance]);

	return (
		<>
			{matomoInstance ? (
				<MatomoProvider value={matomoInstance}>
					{element}
					{reactQueryDevtoolsEnabled && (
						<ReactQueryDevtools
							initialIsOpen={false}
							position="bottom-right"
						/>
					)}
				</MatomoProvider>
			) : (
				<>
					{element}
					{reactQueryDevtoolsEnabled && (
						<ReactQueryDevtools
							initialIsOpen={false}
							position="bottom-right"
						/>
					)}
				</>
			)}
		</>
	);
};

export default App;
